import { createSlice } from "@reduxjs/toolkit"
import { generateThunkHandler } from "@/utils/redux/generateThunkHandler"
import { EnumEndpointsName } from "@/constants/app/type"
import { EnumReduces } from "@/lib/redux/type"
import { initialStateHotel } from "./initialState"
import { HotelLocation, HotelRoom } from "./type"
import { hotelEnpoints } from "./endpoints"
import { callbackHotelDetail } from "./callbacks/callbackHotelDetail"
import { callbackHotelResources } from "./callbacks/callbackHotelResources"
import { callbackFilterOptions } from "./callbacks/callbackFilterOptions"
import {
  buildPopularDomesticGroups,
  buildPopularInternationalGroups,
} from "@/utils/hotel/groupDestinationsByCity"
import { clampHotelRoomsNum, normalizeHotelGuestCounts } from "@/utils/hotel/hotelGuestCounts"
const hotelSlice = createSlice({
  name: EnumReduces.HOTEL,
  initialState: initialStateHotel,
  reducers: {
    changeMode: (state, { payload }) => {
      state.mode = payload
    },
    setFirstLoading: (state, { payload }) => {
      state.firstLoadingHotels = payload
    },
    changeDetailMode: (state, { payload }) => {
      state.modeDetail = payload
    },
    setFormSearch: (state, { payload }) => {
      if (!state.formSearch.init) {
        state.formSearch = {
          ...state.formSearch,
          ...payload,
          location: {
            ...state.formSearch.location,
            ...payload.location
          },
          currentLocation: {
            ...state.formSearch.currentLocation,
            ...payload.currentLocation
          },
          init: true
        }
      }
    },
    setPopularDestinations: (state, { payload: { vietnam, international, show } }) => {
      const hydrateLocations = (destinations: HotelLocation[]) => {
        for (const dest of destinations) {
          state.hotels_locations_search.data[dest._id] = dest
        }
      }

      if (vietnam?.length) {
        state.vietnamPopularDestinations = vietnam
        hydrateLocations(vietnam)
        state.popularGroupsInternal = buildPopularDomesticGroups(vietnam)
      }

      if (international?.length) {
        state.internationalPopularDestinations = international
        hydrateLocations(international)
        state.popularGroupsExternal = buildPopularInternationalGroups(international)
      }

      if (show === "INTERNAL") {
        state.hotels_locations_search.primaryKeys = state.vietnamPopularDestinations.map(
          (dest) => dest._id,
        )
      } else if (show === "EXTERNAL") {
        state.hotels_locations_search.primaryKeys = state.internationalPopularDestinations.map(
          (dest) => dest._id,
        )
      }
    },
    setVietNamPopularDestinations: (state, { payload }) => {
      state.vietnamPopularDestinations = payload
    },
    setSearchType: (state, { payload }) => {
      state.typeSearch = payload
    },
    setInternationalPopularDestinations: (state, { payload }) => {
      state.internationalPopularDestinations = payload
    },
    setChildrenAges: (state, { payload }) => {
      state.formSearch.childrenAges = payload
    },
    updateSearchingDate: (state, { payload }) => {
      state.searchingDestination = payload
    },
    selectDestination: (state, { payload }) => {
      state.formSearch.location = payload
    },
    selectCurrentDestination: (state, { payload }) => {
      state.formSearch = {
        ...state.formSearch,
        currentLocation: payload
      }
    },
    selectDateCheckIn: (state, { payload }) => {
      state.formSearch.checkInDate = payload
    },
    selectDateCheckOut: (state, { payload }) => {
      state.formSearch.checkOutDate = payload
    },
    updateAdults: (state, { payload }) => {
      const roomsNum = state.formSearch.roomsNum || 1
      state.formSearch.adults = Math.max(roomsNum, Number(payload) || roomsNum)
    },
    updateChildren: (state, { payload }) => {
      state.formSearch.children = payload
    },
    updateRooms: (state, { payload }) => {
      state.formSearch.roomsNum = clampHotelRoomsNum(payload)
      normalizeHotelGuestCounts(state.formSearch)
    },
    selectFilterOption: (state, { payload }) => {
      if (!state.formSearch.filter[payload.category]) {
        state.formSearch.filter[payload.category] = {
          category: payload.category,
          selectOption: [payload.value]
        }
      } else if (state.formSearch.filter[payload.category].selectOption.indexOf(payload.value) < 0) {
        state.formSearch.filter[payload.category].selectOption.push(payload.value)
      } else {
        state.formSearch.filter[payload.category].selectOption = state.formSearch.filter[payload.category].selectOption.filter((v: any) => v !== payload.value)
      }
    },
    clearFilterOption: (state, { payload }) => {
      delete state.formSearch.filter[payload.category]
    },
    selectSortOption: (state, { payload }) => {
      state.formSearch.sort = {
        selectOption: payload
      }
    },
    setSortData: (state, { payload }) => {
      state.hotels_sort_option.data = payload
      state.hotels_sort_option.loading = false
      state.hotels_sort_option.error = null
      state.hotels_sort_option.called = true
    },
    setFilterData: (state, { payload }) => {
      callbackFilterOptions && callbackFilterOptions(state, { payload }, EnumEndpointsName.HOTEL_FILTER_OPTION)
    },
    inputFilterRange: (state, { payload }) => {
      state.formSearch.filter[payload.category] = {
        category: payload.category,
        selectOption: payload.value
      }
    },
    setHotel: (state, { payload: { hotel, resource: { roomData } } }) => {
      if (Object.keys(state.hotels_detail.data).length === 0) {
        state.hotels_detail.data = {
          ...hotel,
          searchResponse: {
            ...hotel.searchResponse,
            rooms: hotel.searchResponse.rooms.reduce((obj: any, room: HotelRoom) => {
              const roomResources = roomData.find((d: any) => d.hotelRoomTypeId == room.roomId)
              if (!obj[room.roomId]) {
                obj[room.roomId] = [
                  {
                    ...room,
                    resource: roomResources
                  }
                ]
              } else {
                obj[room.roomId].push({
                  ...room,
                  resource: roomResources
                })
              }
              return obj
            }, {} as any)
          }
        }
      }
    },
    setHotelResource: (state, { payload }) => {
      if (Object.keys(state.hotels_detail_resource.data).length <= 0) {
        state.hotels_detail_resource.data = payload
      }
    },
    resetHotelsSearch: (state) => {
      if (state.mode === 'maps') {
        state.hotels_search.primaryKeysForMaps = []
      } else {
        state.hotels_search.primaryKeys = []
        state.hotels_search.feePolicies = []
      }
      
      if (!!state.formSearch.location) {
        state.formSearch.currentLocation = {
          ...state.formSearch.location
        }
      }
    },
    setDataHotelDetail: (state, { payload: { detail, resources } }) => {
      callbackHotelDetail && callbackHotelDetail(state, { payload: detail }, "")
      callbackHotelResources && callbackHotelResources(state, { payload: resources }, "")
    },
    setHotelMasterDetail: (state, { payload }) => {
      state.hotel_master_detail.data = payload
      state.hotel_master_detail.called = true
      state.hotel_master_detail.loading = false
      state.hotel_master_detail.error = null
    }
  },
  extraReducers(builder) {
    generateThunkHandler(builder, EnumReduces.HOTEL, Object.keys(hotelEnpoints) as EnumEndpointsName[])
  },
})

export const hotelActions = hotelSlice.actions

export default hotelSlice.reducer